/**
 * permessiConsoleService — i Permessi edilizi visti dalla console, in SOLA LETTURA (PERMESSI-EDILIZI-MODEL §10, piano 5
 * passo 5.8): la salute degli albi dei comuni, i documenti che il sistema non è riuscito a leggere (per motivo), quante
 * pratiche sono arrivate.
 *
 * Il super utente legge le raccolte di piattaforma per la regola generale della console (le aziende no: per loro
 * passano solo dalle funzioni del server). Niente dati delle persone private: lo spazio protetto non si legge da qui.
 * I conteggi sono conteggi del server (`getCountFromServer`): non si scaricano i documenti per contarli.
 */
import { collection, documentId, getCountFromServer, getDocs, orderBy, query, endAt, startAt, where } from 'firebase/firestore';
import { db } from '../firebase';
import type { FonteAlbo, MotivoNonLetta } from '@shared/permessi/albi';
import type { StatoLettore } from '@shared/gare/tipi';
import { VERSIONE_CLASSIFICAZIONE } from '@shared/permessi/categorieLavoro';
import type { TenantCapabilitySnapshot } from '@shared/capabilities/capabilityService';
import { aziendeDelModulo, inizioDelMese, type AlboDelComune, type AziendaDelModulo } from './permessiConsoleRegole';

export { albiInOrdine, type AlboDelComune, type AziendaDelModulo } from './permessiConsoleRegole';

const TAG = '[console/permessi]';


export const MOTIVI_NON_LETTA: readonly MotivoNonLetta[] = ['documento_assente', 'troppo_grande', 'scansione', 'piattaforma_sconosciuta', 'formato_non_riconosciuto', 'chiave_mancante', 'documento_illeggibile'];

export interface QuadroPermessi {
  albi: AlboDelComune[];
  pubblicazioni: { da_leggere: number; lette: number; non_lette: Record<MotivoNonLetta, number> };
  /** `classificate`: con la classificazione della versione di oggi (piano 8); le altre si vedono «da verificare». */
  pratiche: { correnti: number; storico: number; del_mese: number; classificate: number };
  sblocchi_del_mese: number;
  /** Le aziende col modulo e i prodotti per cui lo usano (piano 8). */
  aziende: AziendaDelModulo[];
}

const PREFISSO_ALBO = 'permessi_albo_';
const PREFISSO_ARCHIVIO = 'permessi_storico_';


const conta = async (q: Parameters<typeof getCountFromServer>[0]): Promise<number> => (await getCountFromServer(q)).data().count;

export async function leggiQuadroPermessi(adesso = new Date()): Promise<QuadroPermessi> {
  const inizio = performance.now();
  const mese = inizioDelMese(adesso);
  const pubblicazioni = collection(db, 'permessi_pubblicazioni');
  const pratiche = collection(db, 'permessi_pratiche');
  const [fonti, lettori, archivi, daLeggere, lette, nonLette, correnti, storico, delMese, sblocchi, classificate, tenants, prodotti] = await Promise.all([
    getDocs(collection(db, 'permessi_fonti')),
    getDocs(query(collection(db, 'gare_connettori'), orderBy(documentId()), startAt(PREFISSO_ALBO), endAt(`${PREFISSO_ALBO}`))),
    getDocs(query(collection(db, 'gare_connettori'), orderBy(documentId()), startAt(PREFISSO_ARCHIVIO), endAt(`${PREFISSO_ARCHIVIO}`))),
    conta(query(pubblicazioni, where('stato', '==', 'da_leggere'))),
    conta(query(pubblicazioni, where('stato', '==', 'letta'))),
    Promise.all(MOTIVI_NON_LETTA.map((m) => conta(query(pubblicazioni, where('stato', '==', 'non_letta'), where('motivo', '==', m))))),
    conta(query(pratiche, where('origine', '==', 'corrente'))),
    conta(query(pratiche, where('origine', '==', 'storico'))),
    // Un cantiere conta dal giorno in cui è diventato di oggi (`corrente_dal`): le pratiche lette dall'archivio non l'hanno.
    conta(query(pratiche, where('corrente_dal', '>=', mese))),
    conta(query(collection(db, 'permessi_sblocchi'), where('sbloccato_il', '>=', mese))),
    conta(query(pratiche, where('classificazione.versione', '==', VERSIONE_CLASSIFICAZIONE))),
    getDocs(collection(db, 'tenants')),
    getDocs(collection(db, 'permessi_aziende')),
  ]);
  const prodottiPerAzienda: Record<string, string[]> = {};
  prodotti.forEach((d) => {
    const v = d.data() as { azienda_id?: unknown; prodotti?: unknown };
    if (v.azienda_id === d.id && Array.isArray(v.prodotti)) prodottiPerAzienda[d.id] = v.prodotti.filter((x): x is string => typeof x === 'string');
  });
  const aziende = aziendeDelModulo(
    tenants.docs.map((d) => {
      const t = d.data() as TenantCapabilitySnapshot & { nome_azienda?: unknown; name?: unknown };
      return { id: d.id, nome: String(t.nome_azienda ?? t.name ?? d.id), dati: t };
    }),
    prodottiPerAzienda,
  );
  const perCf = new Map<string, StatoLettore>();
  lettori.forEach((d) => perCf.set(d.id.slice(PREFISSO_ALBO.length), { ...(d.data() as StatoLettore), id: d.id }));
  const archivioPerCf = new Map<string, StatoLettore>();
  archivi.forEach((d) => archivioPerCf.set(d.id.slice(PREFISSO_ARCHIVIO.length), { ...(d.data() as StatoLettore), id: d.id }));
  const albi = fonti.docs.map((d) => {
    const fonte = d.data() as FonteAlbo;
    return { fonte, lettore: perCf.get(fonte.cf) || null, archivio: archivioPerCf.get(fonte.cf) || null };
  });
  const non_lette = {} as Record<MotivoNonLetta, number>;
  MOTIVI_NON_LETTA.forEach((m, i) => (non_lette[m] = nonLette[i]));
  console.info(TAG, { comuni: albi.length, ms: Math.round(performance.now() - inizio) });
  return { albi, pubblicazioni: { da_leggere: daLeggere, lette, non_lette }, pratiche: { correnti, storico, del_mese: delMese, classificate }, sblocchi_del_mese: sblocchi, aziende };
}
